import { useMemo } from 'react';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import type { Permission } from '@src/shared/constants/permissions';

/**
 * Hook to check user permissions
 * Provides utilities to check if the current user has specific permissions
 */
export const usePermission = () => {
    const { user } = useAuth();

    const permissions = useMemo(() => {
        return user?.permissions || [];
    }, [user]);

    const roles = useMemo(() => {
        return user?.roles || [];
    }, [user]);

    /**
     * Check if user has a specific permission
     * @param permission - Permission string to check
     * @returns boolean - true if user has the permission
     */
    const hasPermission = (permission: Permission | string): boolean => {
        if (!user) return false;
        // Check for wildcard permission (super admin)
        if (permissions.includes('*')) return true;
        return permissions.includes(permission);
    };

    /**
     * Check if user has all of the specified permissions
     * @param perms - Array of permission strings
     * @returns boolean - true if user has all permissions
     */
    const hasAllPermissions = (perms: (Permission | string)[]): boolean => {
        if (!user) return false;
        // Check for wildcard permission (super admin)
        if (permissions.includes('*')) return true;
        return perms.every(perm => permissions.includes(perm));
    };

    /**
     * Check if user has any of the specified permissions
     * @param perms - Array of permission strings
     * @returns boolean - true if user has at least one permission
     */
    const hasAnyPermission = (perms: (Permission | string)[]): boolean => {
        if (!user) return false;
        // Check for wildcard permission (super admin)
        if (permissions.includes('*')) return true;
        return perms.some(perm => permissions.includes(perm));
    };

    /**
     * Check if user has a specific role
     * @param roleName - Role name to check
     * @returns boolean - true if user has the role
     */
    const hasRole = (roleName: string): boolean => {
        if (!user) return false;
        return roles.some(role => role.name === roleName);
    };

    /**
     * Check if user is super admin (has wildcard permission)
     * @returns boolean - true if user is super admin
     */
    const isSuperAdmin = (): boolean => {
        if (!user) return false;
        return permissions.includes('*') || roles.some(role => role.name === 'super_admin');
    };

    return {
        permissions,
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRole,
        isSuperAdmin,
    };
};
